import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { KeyHandlerDirective } from '../../../../directives/key-handler.directive';
import { CodingFieldsService } from '../../../../services/coding-fields.service';
import { CodingField } from '../../../../models/field/coding-field.model';
import { FieldType } from '../../../../models/field/field-type.enum';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { map, concatMap } from 'rxjs/operators';
import { BuiltInFieldsService } from 'src/app/services/built-in-fields.service';
import { Field } from 'src/app/models/field/field.model';
import { Guid } from '../../../../models/guid';
import { PagingResponse } from '@models/paging-response.model';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
    selector: 'lex-doc-viewer-add-coding-field',
    templateUrl: './doc-viewer-add-coding-field.component.html',
    styleUrls: ['./doc-viewer-add-coding-field.component.scss'],
    animations: [
        trigger('appear-error', [
            transition('void => *', [
                style({ opacity: 0, transform: 'translateY(-4px)' }),
                animate(150, style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            transition('* => void', [
                animate(150, style({ opacity: 0, transform: 'translateY(-4px)' }))
            ])
        ])
    ],
    standalone: false
})
export class DocViewerAddCodingFieldComponent extends KeyHandlerDirective implements OnInit, OnDestroy {
	public isNewSection: boolean = false;
	public parentSectionId: string = '';
	public sections: CodingField[] = [];
	public fieldsSorted: CodingField[] = [];
	public fieldToSave: CodingField = <CodingField>{
		parentFieldId: Guid.Empty,
		caseId: Guid.Empty,
		displayTitle: '',
		fieldId: Guid.Empty,
		rank: 0,
		type: FieldType.TextField
	};
	public parentSection: CodingField = <CodingField>{
		parentFieldId: Guid.Empty,
		caseId: Guid.Empty,
		displayTitle: '',
		fieldId: Guid.Empty,
		rank: 0,
		type: FieldType.SectionSeparator
	};
	public fieldForm: UntypedFormGroup;

	public fieldTypes: Array<{
		value: FieldType,
		displayName: string
	}> = [
		{
			value: FieldType.TextField,
			displayName: 'Text Field'
		},
		{
			value: FieldType.CheckBox,
			displayName: 'Checkbox Field'
		},
		{
			value: FieldType.SharedCheckBox,
			displayName: 'Shared Checkbox Field'
		}
	];

	public builtInFields: Field[] = [];

	private subscription = new Subscription();

	constructor(
		private codingFieldService: CodingFieldsService,
		private fb: UntypedFormBuilder,
		private builtInFieldsService: BuiltInFieldsService,
		private dialogRef: MatDialogRef<DocViewerAddCodingFieldComponent>) {
		super();
	}

	public ngOnInit() {
		this.subscription.add(
			this.codingFieldService.getFields().subscribe((data: PagingResponse<CodingField>) => {
				if (data) {
					this.fieldsSorted = data.value.sort((a: CodingField, b: CodingField) => a.rank > b.rank ? 1 : -1);
					this.sections = this.fieldsSorted.filter(f => f.type === FieldType.SectionSeparator);
				}
			}));

		this.fieldForm = this.fb.group({
			selectParentSelection: new UntypedFormControl(),
			newParentSection: new UntypedFormControl('', [Validators.required, this.duplicateTitle()]),
			newFieldTitle: new UntypedFormControl('', [Validators.required, this.duplicateTitle()]),
			fieldType: new UntypedFormControl(FieldType.TextField, [Validators.required])
		});

		this.builtInFieldsService.getFields().subscribe(data => this.builtInFields = data.value);
	}

	public ngOnDestroy() {
		this.subscription.unsubscribe();
	}

	public ok() {
		if (this.fieldForm.invalid) {
			// User pressed enter key without completing the form - just close the dialog
			this.dialogRef.close(null);
			return;
		}

		this.fieldToSave.displayTitle = this.fieldForm.get('newFieldTitle').value;
		this.fieldToSave.type = this.fieldForm.get('fieldType').value;

		if (this.isNewSection) {
			this.parentSection.displayTitle = this.fieldForm.get('newParentSection').value;
			this.parentSection.type = FieldType.SectionSeparator;
			this.parentSection.rank = this.fieldsSorted.length > 0 ? this.fieldsSorted[this.fieldsSorted.length - 1].rank + 1 : 1;

			this.codingFieldService.addCustomField(this.parentSection).pipe(
				map((r: CodingField) => {
					this.fieldToSave.parentFieldId = r.fieldId;
					this.fieldToSave.rank = r.rank + 1;
				}),
				concatMap(() => this.codingFieldService.addCustomField(this.fieldToSave))
			).subscribe(() => {
				this.dialogRef.close(this.fieldToSave)
			});
		} else {
			this.parentSection = this.fieldsSorted.find(f => f.fieldId === this.parentSectionId);
			this.fieldToSave.parentFieldId = this.parentSection.fieldId;

			let rank = this.parentSection.rank + 1;
			const childFields = this.fieldsSorted.filter(f => f.parentFieldId === this.parentSection.fieldId);
			if (childFields.length > 0)
				rank = childFields[childFields.length - 1].rank + 1;

			this.fieldToSave.rank = rank;

			this.codingFieldService.addCustomField(this.fieldToSave).subscribe((field: CodingField) => {
				if (field)
					this.fieldToSave = field;
				this.dialogRef.close(this.fieldToSave);
			});
		}
	}

	public sectionChanged($event) {
		this.parentSectionId = $event.value;
		this.isNewSection = false;
		this.fieldForm.get('newParentSection').reset();
		this.setValidators();
	}

	public createNewParent() {
		this.isNewSection = !this.isNewSection;
		this.parentSectionId = null;
		this.fieldForm.get('selectParentSelection').reset();
		this.setValidators();
	}

	public setValidators() {
		const parentSelectControl = this.fieldForm.get('selectParentSelection');
		const newParentControl = this.fieldForm.get('newParentSection');

		if (this.isNewSection) {
			parentSelectControl.setValidators(null);
			newParentControl.setValidators([Validators.required, this.duplicateTitle()]);
		} else {
			parentSelectControl.setValidators([Validators.required]);
			newParentControl.setValidators(null);
		}

		if (!newParentControl.touched) {
			newParentControl.markAsPristine();
		}

		parentSelectControl.updateValueAndValidity();
		newParentControl.updateValueAndValidity();
	}

	public duplicateTitle(): ValidatorFn {
		return (control: AbstractControl): {
			[key: string]: any
		} | null => {
			const forbidden = this.isDuplicateTitle(control.value);
			return forbidden ? {'duplicateTitle': {value: control.value}} : null;
		};
	}

	public isDuplicateTitle(title: string): boolean {
		const allTitles: string[] = new Array<string>();
		this.builtInFields.forEach(f => {
			allTitles.push(f.displayTitle.toLowerCase());
			allTitles.push(f.databaseTitle.toLowerCase());
		});
		this.fieldsSorted.forEach(cf => allTitles.push(cf.displayTitle.toLowerCase()));
		if (this.isNewSection) {
			const parentValue = this.fieldForm.get('newParentSection').value.toLowerCase();
			const fieldValue = this.fieldForm.get('newFieldTitle').value.toLowerCase();
			return parentValue === fieldValue || allTitles.includes(title?.toLowerCase());
		}
		return allTitles.includes(title?.toLowerCase());
	}
}
